import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Eye,
  Plus,
  MessageSquare,
  Image as ImageIcon,
  CheckCircle2,
  Clock,
  AlertCircle,
  Trash2,
  Copy,
  Check,
} from "lucide-react";

const statusConfig = {
  open: { label: "Open", color: "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/20", icon: Clock },
  approved: { label: "Approved", color: "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/20", icon: CheckCircle2 },
  closed: { label: "Closed", color: "bg-muted text-muted-foreground border-border", icon: AlertCircle },
};

function StatusBadge({ status }) {
  const cfg = statusConfig[status] || statusConfig.open;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function CopyLinkButton({ token }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/review/${token}`;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 text-xs gap-1.5"
      onClick={async (e) => {
        e.stopPropagation();
        await navigator.clipboard.writeText(url);
        setCopied(true);
        toast.success("Client review link copied");
        setTimeout(() => setCopied(false), 2000);
      }}
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? "Copied" : "Copy client link"}
    </Button>
  );
}

function NewSessionDialog({ open, onOpenChange, onCreated }) {
  const [programs, setPrograms] = useState([]);
  const [programId, setProgramId] = useState("");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    apiClient.get("/programs").then((res) => setPrograms(res.data)).catch(() => {});
  }, [open]);

  const reset = () => {
    setProgramId("");
    setTitle("");
  };

  const handleSubmit = async () => {
    if (!programId) return toast.error("Select a program");
    if (!title.trim()) return toast.error("Title is required");
    const program = programs.find((p) => p.id === programId);
    setSaving(true);
    try {
      const res = await apiClient.post("/review-studio/sessions", {
        program_id: programId,
        program_name: program?.program_name || "",
        title: title.trim(),
      });
      toast.success("Review session created");
      reset();
      onOpenChange(false);
      onCreated(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create session");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            New Review Session
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Program *</Label>
            <Select value={programId} onValueChange={setProgramId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a program" />
              </SelectTrigger>
              <SelectContent>
                {programs.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.program_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Title *</Label>
            <Input
              placeholder="e.g. Kitchen Renders v1"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving}>{saving ? "Creating…" : "Create Session"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SessionCard({ session, onOpen, onDelete }) {
  return (
    <Card className="border border-border hover:border-primary/40 transition-colors cursor-pointer" onClick={() => onOpen(session.id)}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-medium text-sm truncate">{session.title}</p>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">{session.program_name || "No program"}</p>
          </div>
          <StatusBadge status={session.status} />
        </div>

        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" />{session.asset_count} asset{session.asset_count === 1 ? "" : "s"}</span>
          <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" />{session.comment_count} comment{session.comment_count === 1 ? "" : "s"}</span>
        </div>

        <div className="flex items-center gap-2 pt-1">
          <CopyLinkButton token={session.client_token} />
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive ml-auto"
            onClick={(e) => { e.stopPropagation(); onDelete(session); }}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ReviewStudio() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await apiClient.get("/review-studio/sessions");
      setSessions(res.data);
    } catch (e) {
      toast.error("Failed to load review sessions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.delete(`/review-studio/sessions/${deleteTarget.id}`);
      setSessions((prev) => prev.filter((s) => s.id !== deleteTarget.id));
      toast.success("Review session deleted");
    } catch (e) {
      toast.error("Failed to delete session");
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Review Studio
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Share renders, videos & PDFs with clients for pinned feedback and approval
          </p>
        </div>
        <Button size="sm" className="ml-auto gap-1.5" onClick={() => setCreateOpen(true)}>
          <Plus className="w-3.5 h-3.5" />
          New Session
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
      ) : sessions.length === 0 ? (
        <Card className="border border-dashed border-border">
          <CardContent className="flex flex-col items-center justify-center py-12 gap-3">
            <Eye className="w-8 h-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No review sessions yet</p>
            <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)} className="gap-1.5">
              <Plus className="w-3.5 h-3.5" />
              Create first session
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {sessions.map((s) => (
            <SessionCard key={s.id} session={s} onOpen={(id) => navigate(`/review-studio/${id}`)} onDelete={setDeleteTarget} />
          ))}
        </div>
      )}

      <NewSessionDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(s) => { setSessions((prev) => [s, ...prev]); navigate(`/review-studio/${s.id}`); }}
      />

      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-4 h-4" />
              Delete Review Session
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            Delete <span className="font-medium text-foreground">{deleteTarget?.title}</span> and all its assets/comments? This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
